import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { getUrl } from "../../helpers/getUrl";

const initialState = {
  errorMessage: [],
  successMessage: "",
  settingsActiveTab: "contact",
  contactActiveTab: "syncRules",
  invoiceActiveTab: "syncRules",
  productActiveTab: "syncRules",
  isTripletexConnected: false,
  txToken: "",
  selectedTabValues: {},
  saveModalPreference: false,
  status: {
    loading: false,
    status: {},
    error: "",
    currentRequestId: undefined,
  },
};

export const fetchStatus = createAsyncThunk(
  "preference/fetchStatus",
  async (portalId, { dispatch }) => {
    try {
      const { data: response } = await axios.get(
        `${getUrl("REACT_APP_FETCH_STATUS")}?portalId=${portalId}`
      );
      return response;
    } catch (error) {
      let errorMessage;
      if (error?.response && error.response?.data?.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      dispatch(
        setShowErrorModal({
          message: errorMessage,
        })
      );
      throw error;
    }
  }
);

const preferenceSlice = createSlice({
  name: "preference",
  initialState,
  reducers: {
    /**
     * @description Reducer updates the states of success and error modals
     */
    setShowSuccessModal(state, { payload }) {
      state.successMessage = payload.message;
    },
    onHideSuccessModal(state) {
      state.successMessage = "";
    },
    setShowErrorModal(state, { payload }) {
      state.errorMessage.push(payload.message);
    },
    onHideErrorModal(state) {
      state.errorMessage.shift();
    },
    onSetTxToken(state, { payload }) {
      state.txToken = payload.token;
    },
    onSetConnectionPreference(state, { payload }) {
      state.isTripletexConnected = payload.connection;
    },
    onChangeSaveModalPreference(state, { payload }) {
      state.saveModalPreference = payload.show;
    },
    /**
     * @description Reducer updates the state of tab and subTab when user clicks on save from the tab
     */
    onChangeSelectedTabValues(state) {
      window.scrollTo(0, 0);
      state.settingsActiveTab = state.selectedTabValues.tab;
      if (state.selectedTabValues.tab === "contact") {
        state.contactActiveTab = state.selectedTabValues.subTab;
      } else if (state.selectedTabValues.tab === "order") {
        state.invoiceActiveTab = state.selectedTabValues.subTab;
      } else if (state.selectedTabValues.tab === "product") {
        state.productActiveTab = state.selectedTabValues.subTab;
      }
    },
    /**
     * @description Reducer updates the states of active tab keys when user selects the tab or clicked on next button
     */
    onChangeSettingTabKey(state, { payload }) {
      if (
        state.isTripletexConnected &&
        !payload.setupLoading &&
        state.settingsActiveTab === "contact" &&
        (payload.selectedTab === "order" ||
          payload.selectedTab === "product") &&
        (!payload.isContactSyncRulesSaved || !payload.isContactMappingsSaved)
      ) {
        state.selectedTabValues = {
          tab: payload.selectedTab,
          subTab: "syncRules",
        };
        state.saveModalPreference = true;
      } else if (
        state.isTripletexConnected &&
        !payload.setupLoading &&
        state.settingsActiveTab === "order" &&
        (payload.selectedTab === "contact" ||
          payload.selectedTab === "product") &&
        (!payload.isInvoiceSyncRulesSaved || !payload.isInvoiceMappingsSaved)
      ) {
        state.selectedTabValues = {
          tab: payload.selectedTab,
          subTab: "syncRules",
        };
        state.saveModalPreference = true;
      } else if (
        state.isTripletexConnected &&
        !payload.setupLoading &&
        state.settingsActiveTab === "product" &&
        (payload.selectedTab === "contact" ||
          payload.selectedTab === "order") &&
        (!payload.isProductSyncRulesSaved ||
          !payload.isProductFieldMappingsSaved)
      ) {
        state.selectedTabValues = {
          tab: payload.selectedTab,
          subTab: "syncRules",
        };
        state.saveModalPreference = true;
      } else {
        state.settingsActiveTab = payload.selectedTab;
      }
    },

    onChangeContactTab(state, { payload }) {
      if (
        state.isTripletexConnected &&
        !payload.setupLoading &&
        state.contactActiveTab === "syncRules" &&
        !payload.isContactSyncRulesSaved &&
        (payload.selectedTab === "defaultMappings" ||
          payload.selectedTab === "fieldMappings")
      ) {
        state.selectedTabValues = {
          tab: "contact",
          subTab: payload.selectedTab,
        };
        state.saveModalPreference = true;
      } else if (
        state.isTripletexConnected &&
        !payload.setupLoading &&
        state.contactActiveTab === "fieldMappings" &&
        !payload.isContactMappingsSaved &&
        (payload.selectedTab === "syncRules" ||
          payload.selectedTab === "defaultMappings")
      ) {
        state.selectedTabValues = {
          tab: "contact",
          subTab: payload.selectedTab,
        };
        state.saveModalPreference = true;
      } else {
        state.contactActiveTab = payload.selectedTab;
      }
    },

    onChangeInvoiceTab(state, { payload }) {
      if (
        state.isTripletexConnected &&
        !payload.setupLoading &&
        state.invoiceActiveTab === "syncRules" &&
        !payload.isInvoiceSyncRulesSaved &&
        (payload.selectedTab === "fieldMappings" ||
          payload.selectedTab === "defaultMappings")
      ) {
        state.selectedTabValues = {
          tab: "order",
          subTab: payload.selectedTab,
        };
        state.saveModalPreference = true;
      } else if (
        state.isTripletexConnected &&
        !payload.setupLoading &&
        state.invoiceActiveTab === "fieldMappings" &&
        !payload.isInvoiceMappingsSaved &&
        (payload.selectedTab === "syncRules" ||
          payload.selectedTab === "defaultMappings")
      ) {
        state.selectedTabValues = {
          tab: "order",
          subTab: payload.selectedTab,
        };
        state.saveModalPreference = true;
      } else {
        state.invoiceActiveTab = payload.selectedTab;
      }
    },

    onChangeProductTab(state, { payload }) {
      if (
        state.isTripletexConnected &&
        !payload.setupLoading &&
        state.productActiveTab === "syncRules" &&
        !payload.isProductSyncRulesSaved &&
        payload.selectedTab === "fieldMappings"
      ) {
        state.selectedTabValues = {
          tab: "product",
          subTab: payload.selectedTab,
        };
        state.saveModalPreference = true;
      } else if (
        state.isTripletexConnected &&
        !payload.setupLoading &&
        state.productActiveTab === "fieldMappings" &&
        !payload.isProductFieldMappingsSaved &&
        payload.selectedTab === "syncRules"
      ) {
        state.selectedTabValues = {
          tab: "product",
          subTab: payload.selectedTab,
        };
        state.saveModalPreference = true;
      } else {
        state.productActiveTab = payload.selectedTab;
      }
    },

    onUpdateInvoiceActiveTab(state, { payload }) {
      state.invoiceActiveTab = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchStatus.pending, (state, action) => {
      if (!state.status.loading) {
        state.status.loading = true;
        state.status.currentRequestId = action.meta.requestId;
      }
    });

    builder.addCase(fetchStatus.fulfilled, (state, action) => {
      if (
        state.status.loading &&
        action.meta.requestId === state.status.currentRequestId
      ) {
        state.status.loading = false;
        state.status.status = action.payload;
        state.status.error = "";
        state.status.currentRequestId = undefined;

        const { creds } = state.status?.status;

        if (creds) {
          state.isTripletexConnected = creds.tripletex;
        }
      }
    });

    builder.addCase(fetchStatus.rejected, (state, action) => {
      if (
        state.status.loading &&
        action.meta.requestId === state.status.currentRequestId
      ) {
        state.status.loading = false;
        state.status.status = {};
        state.status.error = action.error.message ?? "";
        state.status.currentRequestId = undefined;
      }
    });
  },
});

export default preferenceSlice.reducer;
export const {
  setShowSuccessModal,
  setShowErrorModal,
  onHideSuccessModal,
  onHideErrorModal,
  onSetTxToken,
  onSetConnectionPreference,
  onChangeSettingTabKey,
  onChangeSaveModalPreference,
  onChangeSelectedTabValues,
  onChangeProductTab,
  onChangeContactTab,
  onChangeInvoiceTab,
  onUpdateInvoiceActiveTab,
} = preferenceSlice.actions;
